import { useEffect, useState, useCallback } from 'react';
import { notificationWebSocket } from '../services/notificationWebSocket';
import { Notification } from '../services/notificationApi';
import { ThreadMessageData } from '../types/artist';

/**
 * React hook for managing WebSocket notification connection
 * @param isAuthenticated - Whether the user is authenticated
 */
export function useNotificationWebSocket(isAuthenticated: boolean = true) {
  const [isConnected, setIsConnected] = useState(false);
  const [latestNotification, setLatestNotification] = useState<Notification | null>(null);

  useEffect(() => {
    // Only connect if authenticated
    if (!isAuthenticated) {
      console.log('Not connecting to WebSocket: User not authenticated');
      return;
    }

    // Connect to WebSocket
    notificationWebSocket.connect();

    // Set up event handlers
    const unsubscribeConnect = notificationWebSocket.onConnect(() => {
      setIsConnected(true);
      console.log('Connected to notification WebSocket');
    });

    const unsubscribeDisconnect = notificationWebSocket.onDisconnect(() => {
      setIsConnected(false);
      console.log('Disconnected from notification WebSocket');
    });

    const unsubscribeNotification = notificationWebSocket.onNotification((notification) => {
      console.log('Received notification via WebSocket:', notification);
      setLatestNotification(notification);
    });

    const unsubscribeError = notificationWebSocket.onError((error) => {
      console.error('WebSocket error:', error);
    });

    // Cleanup on unmount or when authentication changes
    return () => {
      unsubscribeConnect();
      unsubscribeDisconnect();
      unsubscribeNotification();
      unsubscribeError();
      notificationWebSocket.disconnect();
    };
  }, [isAuthenticated]);

  return {
    isConnected,
    latestNotification,
  };
}

/**
 * React hook for subscribing to real-time notifications
 * @param onNotification - Callback function to handle incoming notifications
 */
export function useNotificationSubscription(
  onNotification: (notification: Notification) => void
) {
  useEffect(() => {
    const unsubscribe = notificationWebSocket.onNotification(onNotification);
    return unsubscribe;
  }, [onNotification]);
}

/**
 * React hook for subscribing to real-time thread messages
 * @param onThreadMessage - Callback function to handle incoming thread messages
 */
export function useThreadMessageSubscription(
  onThreadMessage: (data: ThreadMessageData) => void
) {
  useEffect(() => {
    const unsubscribe = notificationWebSocket.onThreadMessage(onThreadMessage);
    return unsubscribe;
  }, [onThreadMessage]);
}

/**
 * React hook for managing WebSocket connection status
 */
export function useNotificationConnectionStatus() {
  const [isConnected, setIsConnected] = useState(
    notificationWebSocket.isConnected()
  );

  useEffect(() => {
    const unsubscribeConnect = notificationWebSocket.onConnect(() => {
      setIsConnected(true);
    });

    const unsubscribeDisconnect = notificationWebSocket.onDisconnect(() => {
      setIsConnected(false);
    });

    return () => {
      unsubscribeConnect();
      unsubscribeDisconnect();
    };
  }, []);

  const reconnect = useCallback(() => {
    notificationWebSocket.connect();
  }, []);

  return {
    isConnected,
    reconnect,
  };
}
